@if (script === undefined) {
  <div style="display: flex; justify-content: center; align-items: center; height: 25vh;">
    <mat-spinner diameter="50"></mat-spinner>
  </div>
} @else if (script === null) {
  <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 25vh; gap: 1.5rem; padding: 2rem; text-align: center;">
    <mat-icon style="font-size: 64px; color: #f44336;">error_outline</mat-icon>
    <div>
      <h3 style="margin: 0 0 0.5rem 0; color: #f44336; font-size: 1.5rem;">Script not found</h3>
      <p style="margin: 0; color: #666; max-width: 500px; line-height: 1.5;">Something went wrong, try refreshing the page</p>
    </div>
  </div>
} @else if (script) {
  <div style="display: flex; flex-direction: column; gap: 1rem;">
    <!-- Version Management -->
    @if (script.versions && script.versions.length > 0) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>Version List</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div style="display: flex; flex-direction: column;;">
            @for (version of script.versions; track version.version; let last = $last) {
              <div [class.selectedVersion]="isCurrentlyViewedVersion(version.version)" style="display: flex; align-items: center; gap: 1rem; padding: 1rem; border: 1px solid var(--mat-divider-color); border-radius: 8px;">
                <!-- Version Info -->
                <div style="flex: 1;">
                  <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
                    <strong>
                      Version {{version.version}}
                      @if (version.is_draft) {
                           - Draft
                      }
                    </strong>
                    @if (!version.is_draft) {
                      <span [ngClass]="{
                        'status-published': getVersionStatus(version) === 'Published',
                        'status-rejected': getVersionStatus(version) === 'Rejected',
                        'status-pending': getVersionStatus(version) === 'Pending',
                        'status-superseeded': getVersionStatus(version) === 'Superseeded'
                      }" class="status-badge">
                        {{getVersionStatus(version)}}
                      </span>
                    }

                  </div>
                  <div style="font-size: 0.9rem; color: var(--mat-text-secondary-color);">
                    {{version.title}} • {{version.created_at.split(' ')[0]}}
                  </div>
                </div>

                <!-- Actions -->
                <div style="display: flex; gap: 0.5rem; align-items: center;">
                  <button mat-flat-button 
                          [disabled]="isCurrentlyViewedVersion(version.version)"
                          (click)="onViewVersion(version.version)">
                    View Details
                  </button>
                  @for (action of getVersionActions(version); track action.action) {
                    <button mat-raised-button 
                            [class.mat-warn]="action.color === 'warn'"
                            [class.mat-primary]="action.color === 'primary'"
                            [class.mat-accent]="action.color === 'accent'"
                            [disabled]="action.disabled"
                            (click)="onVersionAction(version.version, action.action)">
                      {{action.label}}
                    </button>
                  }
                </div>
              </div>
              @if (!last) {
                <mat-divider></mat-divider>
              }
            }
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- Comments Section -->
    @if (script.versions && script.versions.length > 0) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>Comments</mat-card-title>
          <mat-card-subtitle>Author and staff comments for this version</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div style="display: flex; flex-direction: column; gap: 1rem;">
            <!-- Author Comment -->
              <div>
                <h4 style="margin: 0 0 0.5rem 0; color: var(--mat-text-color);">Author Comment</h4>
                <div style="padding: 1rem; background-color: var(--mat-divider-color); border-radius: 8px; color: var(--mat-text-color);">
                  @if (getCurrentVersionComment('author_comment')) {
                    {{getCurrentVersionComment('author_comment')}}
                  } @else {
                    <p style="color: var(--mat-sys-on-surface-variant);">No author comment</p>
                  }
                </div>
              </div>
            
            <!-- Staff Comment -->
              <div>
                <h4 style="margin: 0 0 0.5rem 0; color: var(--mat-text-color);">Staff Comment</h4>
                <div style="padding: 1rem; background-color: var(--mat-accent-color); border-radius: 8px; color: white;">
                
                  <mat-form-field appearance="outline" style="width: 100%;">
                    <mat-label>Staff Comment</mat-label>
                    <textarea matInput [(ngModel)]="staffComment"></textarea>
                  </mat-form-field>
                  <div style="display: flex; justify-content: flex-end; gap: 0.5rem;">
                    <button 
                      mat-flat-button 
                      (click)="onSaveStaffComment()"
                      [disabled]="staffComment.length == 0 || staffComment == getCurrentVersionComment('staff_comment')"
                    >
                      Save
                    </button>
                  </div>

                </div>
              </div>

              <!-- AI Rating -->
              @if (hasAiRating()) {
                <div style="background-color: var(--mat-sys-error-container); padding: 1rem; border-radius: 8px; color: var(--mat-sys-on-error-container);">
                  <h4 style="margin: 0 0 0.5rem 0; color: var(--mat-text-color);">AI Rating</h4>
                                     <small>The AI has detected some potentially problematic content on {{aiRating.length || 0}} line(s):</small>
                  <mat-list>
                    @for (rating of aiRating; track $index; let last = $last) {
                      <mat-list-item>
                        <div matListItemTitle>{{rating.label}}</div>
                        <div matListItemLine>{{rating.description}}</div>
                        <div matListItemLine style="border-left: 2px solid var(--mat-sys-on-error-container); padding-left: 0.5rem; cursor: pointer; color: var(--mat-sys-error);" 
                             [matTooltip]="'Click to scroll to line ' + rating.line + ' in the script'"
                             [matTooltipClass]="'warning-tooltip'"
                             [matTooltipShowDelay]="500"
                             [matTooltipHideDelay]="0"
                             (click)="scrollToLine(rating.line)">
                          Line {{rating.line}}: Click to view in script
                        </div>
                      </mat-list-item>
                      @if (!last) {
                        <mat-divider></mat-divider>
                      }
                    }
                  </mat-list>
                </div>
              }

              @if (aiRatingError) {
                <div style="background-color: var(--mat-sys-error-container); padding: 1rem; border-radius: 8px; color: var(--mat-sys-on-error-container);">
                  <h4 style="margin: 0 0 0.5rem 0; color: var(--mat-text-color);">AI Rating Error</h4>
                  <p>
                    The AI has encountered an error while rating the script: {{aiRatingError}}<br>
                    This usually indicates that the script is too problematic for the AI to handle.
                  </p>
                </div>
              }

              @if (pendingAiRating) {
                <div>
                  <h4 style="margin: 0 0 0.5rem 0; color: var(--mat-text-color);">AI Rating</h4>
                  <p>The AI is rating the script...</p>
                  <mat-spinner diameter="20"></mat-spinner>
                </div>
              }
        
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- Change Legend and controls -->
    @if (diff || (script.versions && script.versions.length > 0)) {
      <mat-card>
        <mat-card-content>
          <div style="display: flex; gap: 1rem; flex-wrap: wrap;">
            <mat-form-field appearance="outline" style="width: 100%; max-width: 400px;">
              <mat-label>Compare version {{getCurrentVersionNumber()}} with:</mat-label>
              <mat-select [(ngModel)]="selectedCompareVersion" (selectionChange)="onVersionChange()">
                @for (version of availableVersions; track version.version) {
                  <mat-option [value]="version.version">
                    {{version.label}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" style="min-width: 220px;">
              <mat-label>Diff granularity</mat-label>
              <mat-select [(ngModel)]="diffMode" (selectionChange)="onDiffModeChange()">
                @for (mode of diffModes; track mode.value) {
                  <mat-option [value]="mode.value">{{mode.label}}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap; margin-bottom: 0.5rem;">
            <h3 style="margin: 0; font-size: 1.1rem;">Change Legend</h3>
          </div>
          <div style="display: flex; gap: 1.5rem; flex-wrap: wrap; margin-bottom: 1rem;">
            <span style="display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; color: #666;">
              <span style="color: #2e7d32; font-weight: 500;">Green</span> - Added content
            </span>
            <span style="display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; color: #666;">
              <span style="color: #c62828; font-weight: 500;">Red</span> - Removed content
            </span>
            <span style="display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; color: #666;">
              <span style="color: #f57c00; font-weight: 500;">Yellow</span> - Modified line (inline highlights)
            </span>
            <span style="display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; color: #666;">
              <span style="color: #333; font-weight: 500;">Black</span> - Unchanged content
            </span>
          </div>
          @if (script.versions) {
            <small style="color: #666; font-style: italic;">
              Comparing Version {{getCurrentVersionNumber()}} 
              (current) with Version {{selectedCompareVersion}}
            </small>
          }
        </mat-card-content>
      </mat-card>
    }

    <button mat-flat-button routerLink="/scripts/submit/{{script.id}}">Edit Script</button>

    <!-- Main Content -->
    <div style="display: flex; flex-direction: row; gap: 1rem; flex-wrap: wrap;">
      <!-- Basic Information -->
      <mat-card style="min-width: 100%;">
        <mat-card-header>
          <mat-card-title>Basic Information</mat-card-title>
          <mat-card-subtitle>Tell us about the basics of your script</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <mat-action-list>
            <mat-divider></mat-divider>
            @if (diff) {
              @if (diff.title.changed) {
                <app-list-item-display 
                  title="Title" 
                  [oldValue]="diff.title.old"
                  [newValue]="diff.title.updated"
                  [showDiff]="true">
                </app-list-item-display>
              } @else {
                <app-list-item-display 
                  title="Title" 
                  [value]="diff.title.updated">
                </app-list-item-display>
              }
            } @else {
              <app-list-item-display 
                title="Title" 
                [value]="script.title || ''">
              </app-list-item-display>
            }
            <mat-divider></mat-divider>
            <mat-divider></mat-divider>
            @if (diff) {
              @if (diff.categories.changed) {
                <app-list-item-display 
                  title="Categories" 
                  [oldValue]="diff.categories.old.join(', ') || ''"
                  [newValue]="diff.categories.updated.join(', ') || ''"
                  [showDiff]="true">
                </app-list-item-display>
              } @else {
                <app-list-item-display 
                  title="Categories" 
                  [value]="diff.categories.updated.join(', ') || ''">
                </app-list-item-display>
              }
            } @else {
              <app-list-item-display 
                title="Categories" 
                [value]="script.categories.join(', ') || ''">
              </app-list-item-display>
            }
            <mat-divider></mat-divider>
            @if (diff) {
              @if (diff.backdrop.changed) {
                <app-list-item-display 
                  title="Backdrop" 
                  [oldValue]="diff.backdrop.old.name || ''"
                  [newValue]="diff.backdrop.updated.name || ''"
                  [showDiff]="true">
                </app-list-item-display>
              } @else {
                <app-list-item-display 
                  title="Backdrop" 
                  [value]="diff.backdrop.updated.name || ''">
                </app-list-item-display>
              }
            } @else {
              <app-list-item-display 
                title="Backdrop" 
                [value]="script.backdrop.name || ''">
              </app-list-item-display>
            }
          </mat-action-list>
        </mat-card-content>
      </mat-card>

      <!-- Characters -->
      <mat-card style="min-width: 400px; flex-grow: 1;">
        <mat-card-header>
          <mat-card-title>Characters</mat-card-title>
          <mat-card-subtitle>Define what characters will act in your script</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <mat-action-list>
            @if (diff) {
              @if (diff.characters.changed) {
                <!-- Show removed characters -->
                @for (character of diff.characters.diff.removed; track character.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [title]="character.tag + ' - ' + character.name" 
                    [value]="character.location.name"
                    valueClass="removed">
                  </app-list-item-display>
                }
                <!-- Show added characters -->
                @for (character of diff.characters.diff.added; track character.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [title]="character.tag + ' - ' + character.name" 
                    [value]="character.location.name"
                    valueClass="added">
                  </app-list-item-display>
                }
                <!-- Show modified characters -->
                @for (mod of diff.characters.diff.modified; track mod.old.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [oldTitle]="mod.old.tag + ' - ' + mod.old.name" 
                    [newTitle]="mod.new.tag + ' - ' + mod.new.name"
                    [oldValue]="mod.old.location.name"
                    [newValue]="mod.new.location.name"
                    [showModified]="true"
                    [showDiff]="true">
                  </app-list-item-display>
                }
                <!-- Show unchanged characters -->
                @for (character of diff.characters.diff.unchanged; track character.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [title]="character.tag + ' - ' + character.name" 
                    [value]="character.location.name">
                  </app-list-item-display>
                }
              } @else {
                @for (character of diff.characters.updated; track character.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [title]="character.tag + ' - ' + character.name" 
                    [value]="character.location.name">
                  </app-list-item-display>
                }
              }
            } @else {
              @for (character of script.characters || []; track character.tag) {
                <mat-divider></mat-divider>
                <app-list-item-display 
                  [title]="character.tag + ' - ' + character.name" 
                  [value]="character.location.name">
                </app-list-item-display>
              }
            }
          </mat-action-list>
        </mat-card-content>
      </mat-card>

      <!-- Suggestions -->
      <mat-card style="min-width: 400px; flex-grow: 1;">
        <mat-card-header>
          <mat-card-title>Suggestions</mat-card-title>
          <mat-card-subtitle>Let the Audience fill in the blanks</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <mat-action-list>
            @if (diff) {
              @if (diff.suggestions.changed) {
                <!-- Show removed suggestions -->
                @for (suggestion of diff.suggestions.diff.removed; track suggestion.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [value]="suggestion.description" 
                    [title]="suggestion.tag"
                    [details]="'Examples: ' + suggestion.examples.join(', ')"
                    valueClass="removed">
                  </app-list-item-display>
                }
                <!-- Show added suggestions -->
                @for (suggestion of diff.suggestions.diff.added; track suggestion.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [value]="suggestion.description" 
                    [title]="suggestion.tag"
                    [details]="'Examples: ' + suggestion.examples.join(', ')"
                    valueClass="added">
                  </app-list-item-display>
                }
                <!-- Show modified suggestions -->
                @for (mod of diff.suggestions.diff.modified; track mod.old.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [oldTitle]="mod.old.tag" 
                    [newTitle]="mod.new.tag"
                    [oldValue]="mod.old.description"
                    [newValue]="mod.new.description"
                    [oldDetails]="'Examples: ' + mod.old.examples.join(', ')"
                    [newDetails]="'Examples: ' + mod.new.examples.join(', ')"
                    [showModified]="true"
                    [showDiff]="true">
                  </app-list-item-display>
                }
                <!-- Show unchanged suggestions -->
                @for (suggestion of diff.suggestions.diff.unchanged; track suggestion.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [value]="suggestion.description" 
                    [title]="suggestion.tag"
                    [details]="'Examples: ' + suggestion.examples.join(', ')">
                  </app-list-item-display>
                }
              } @else {
                @for (suggestion of diff.suggestions.updated; track suggestion.tag) {
                  <mat-divider></mat-divider>
                  <app-list-item-display 
                    [value]="suggestion.description" 
                    [title]="suggestion.tag"
                    [details]="'Examples: ' + suggestion.examples.join(', ')">
                  </app-list-item-display>
                }
              }
            } @else {
              @for (suggestion of script.suggestions || []; track suggestion.tag) {
                <mat-divider></mat-divider>
                <app-list-item-display 
                  [value]="suggestion.description" 
                  [title]="suggestion.tag"
                  [details]="'Examples: ' + suggestion.examples.join(', ')">
                </app-list-item-display>
              }
            }
          </mat-action-list>
        </mat-card-content>
      </mat-card>

      <!-- Script Content -->
      <mat-card style="min-width: 100%;">
        <mat-card-header>
          <mat-card-title>Script</mat-card-title>
          <mat-card-subtitle>The dialog of your script</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          @if (diff) {
            <div class="script-diff-container">
              <!-- Line numbers container -->
              <div 
                #lineNumbersDiv 
                class="line-numbers-container">
              </div>
              
              <!-- Script content with diff highlighting -->
              <div class="script-content-container" #scriptContentContainer (scroll)="onScriptContentScroll()">
                @for (line of diff.lines; track $index) {
                  <div class="script-line" 
                       [ngClass]="'line-' + line.type"
                       [class.line-warning]="hasWarnings(getActualLineNumber($index))"
                       [matTooltip]="hasWarnings(getActualLineNumber($index)) ? getWarningTooltip(getActualLineNumber($index)) : ''"
                       [matTooltipClass]="'warning-tooltip'"
                       [matTooltipShowDelay]="500"
                       [matTooltipHideDelay]="0">
                    <span class="script-line-content" [innerHTML]="line.html"></span>
                  </div>
                }
              </div>
            </div>
          } @else {
            <div class="script-diff-container">
              <!-- Line numbers container -->
              <div 
                #lineNumbersDiv 
                class="line-numbers-container">
              </div>
              
              <!-- Script content -->
              <div class="script-content-container" #scriptContentContainer (scroll)="onScriptContentScroll()">
                @for (line of (script.content || '').split('\n'); track $index) {
                  <div class="script-line line-unchanged"
                       [class.line-warning]="hasWarnings($index + 1)"
                       [matTooltip]="hasWarnings($index + 1) ? getWarningTooltip($index + 1) : ''"
                       [matTooltipClass]="'warning-tooltip'"
                       [matTooltipShowDelay]="500"
                       [matTooltipHideDelay]="0">
                    <span class="unchanged">{{line}}</span>
                  </div>
                }
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>
    </div>

    <!-- No Diff Message -->
    @if (!diff && script.versions && script.versions.length > 0) {
      <mat-card>
        <mat-card-content style="text-align: center; padding: 2rem;">
          @if (selectedCompareVersion === script.versions[script.versions.length - 1].version) {
            <p style="color: #666; font-style: italic; margin: 0;">Comparing version with itself - no differences found (debug mode).</p>
          } @else {
            <p style="color: #666; font-style: italic; margin: 0;">No differences found between the selected versions.</p>
          }
        </mat-card-content>
      </mat-card>
    }
  </div>
}
